import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { RefreshCw, Search, Plus, Key } from 'lucide-react';
import { apiService, ApiKey } from '../services/api';
import { commitOwnedSearch, HistoryMode } from '../navigation/historyState';
import { parseEnumParam, parseFlagParam } from '../navigation/urlCodec';
import ApiKeyCard from './ApiKeyCard';
import ApiKeyDetailSidebar from './ApiKeyDetailSidebar';
import CustomSelect from './CustomSelect';
import { useTabActivity } from '../contexts/TabActivityContext';
import './ApiKeysTab.css';

const OWNED_KEYS = ['key', 'new', 'q', 'status', 'sort', 'dir'];

export const replaceSelectedApiKey = (
  apiKeys: ApiKey[],
  savedApiKey: ApiKey,
  replacementKeyId: string,
): ApiKey[] => {
  const withoutDuplicateSuccessor = apiKeys.filter(
    (key) => (
      key.key_id !== savedApiKey.key_id
      || key.key_id === replacementKeyId
    ),
  );
  let replaced = false;
  const next = withoutDuplicateSuccessor.map((key) => {
    if (key.key_id !== replacementKeyId) return key;
    replaced = true;
    return savedApiKey;
  });
  return replaced ? next : [savedApiKey, ...next];
};

const ApiKeysTab: React.FC = () => {
  // API Keys state
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [selectedApiKey, setSelectedApiKey] = useState<ApiKey | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [tempApiKey, setTempApiKey] = useState<ApiKey | null>(null);

  // Layout state
  const [sortBy, setSortBy] = useState<'name' | 'created_at' | 'last_used_at'>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [urlNotice, setUrlNotice] = useState<string | null>(null);
  const [urlHydrated, setUrlHydrated] = useState(false);
  const historyModeRef = React.useRef<HistoryMode>('replace');
  const isApplyingUrlRef = React.useRef(false);
  const pendingApiKeyIdRef = React.useRef<string | null | undefined>(undefined);
  const pendingCreateRef = React.useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { isActive } = useTabActivity();
  const isActiveRef = React.useRef(isActive);
  isActiveRef.current = isActive;

  // Sidebar width
  const [sidebarWidth, setSidebarWidth] = useState<number>(() => {
    const saved = localStorage.getItem('apiKeySidebarWidth');
    return saved ? parseInt(saved, 10) : 600;
  });

  // Listen for sidebar width changes
  useEffect(() => {
    const handleSidebarWidthChange = (event: CustomEvent) => {
      setSidebarWidth(event.detail.width);
    };

    window.addEventListener('apiKeySidebarWidthChange' as any, handleSidebarWidthChange);

    return () => {
      window.removeEventListener('apiKeySidebarWidthChange' as any, handleSidebarWidthChange);
    };
  }, []);

  // Load data on mount
  useEffect(() => {
    loadApiKeys();
  }, []);

  useEffect(() => {
    isApplyingUrlRef.current = true;
    const params = new URLSearchParams(location.search);
    setSearchTerm(params.get('q') || '');
    setFilterStatus(params.get('status') || '');
    setSortBy(parseEnumParam(params.get('sort'), ['name', 'created_at', 'last_used_at'] as const, 'created_at'));
    setSortOrder(parseEnumParam(params.get('dir'), ['asc', 'desc'] as const, 'desc'));
    pendingApiKeyIdRef.current = params.get('key');
    pendingCreateRef.current = parseFlagParam(params.get('new'));

    const timer = window.setTimeout(() => {
      isApplyingUrlRef.current = false;
      setUrlHydrated(true);
    }, 0);

    return () => window.clearTimeout(timer);
  }, [location.search]);

  useEffect(() => {
    if (!urlHydrated || (pendingApiKeyIdRef.current === undefined && !pendingCreateRef.current)) return;

    const applyPendingSelection = async () => {
      if (pendingCreateRef.current) {
        pendingCreateRef.current = false;
        pendingApiKeyIdRef.current = undefined;
        handleCreateApiKey();
        return;
      }

      const requestedId = pendingApiKeyIdRef.current;
      if (!requestedId) {
        pendingApiKeyIdRef.current = undefined;
        setShowSidebar(false);
        setSelectedApiKey(null);
        setIsCreating(false);
        setTempApiKey(null);
        return;
      }

      const localMatch = apiKeys.find((apiKey) => apiKey.key_id === requestedId);
      if (localMatch) {
        setSelectedApiKey(localMatch);
        setShowSidebar(true);
        setIsCreating(false);
        setTempApiKey(null);
        pendingApiKeyIdRef.current = undefined;
        return;
      }

      try {
        const fetched = await apiService.getApiKey(requestedId);
        setApiKeys((prev) => (prev.some((item) => item.key_id === fetched.key_id) ? prev : [fetched, ...prev]));
        setSelectedApiKey(fetched);
        setShowSidebar(true);
        setIsCreating(false);
        setTempApiKey(null);
      } catch {
        setUrlNotice('Requested API key no longer exists. Showing API key list.');
        setShowSidebar(false);
        setSelectedApiKey(null);
        historyModeRef.current = 'replace';
        if (isActiveRef.current) commitOwnedSearch({
          location,
          navigate,
          ownedKeys: OWNED_KEYS,
          updates: { key: null, new: null },
          mode: 'replace',
        });
      } finally {
        pendingApiKeyIdRef.current = undefined;
      }
    };

    applyPendingSelection();
  }, [apiKeys, location, navigate, urlHydrated]);

  useEffect(() => {
    if (!isActive || !urlHydrated || isApplyingUrlRef.current || !!pendingApiKeyIdRef.current || pendingCreateRef.current) return;
    const mode = historyModeRef.current;
    historyModeRef.current = 'replace';

    const isTempSelection = !!selectedApiKey?.key_id?.startsWith('temp_');
    commitOwnedSearch({
      location,
      navigate,
      ownedKeys: OWNED_KEYS,
      updates: {
        q: searchTerm || null,
        status: filterStatus || null,
        sort: sortBy,
        dir: sortOrder,
        key: showSidebar && selectedApiKey && !isCreating && !isTempSelection ? selectedApiKey.key_id : null,
        new: showSidebar && isCreating ? 1 : null,
      },
      mode,
    });
  }, [
    filterStatus,
    isActive,
    isCreating,
    location,
    navigate,
    searchTerm,
    selectedApiKey,
    showSidebar,
    sortBy,
    sortOrder,
    urlHydrated,
  ]);

  // Load API keys
  const loadApiKeys = async () => {
    setIsLoading(true);
    try {
      const data = await apiService.getApiKeys();
      setApiKeys(data);
    } catch (error) {
      console.error('Failed to load API keys:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle API key click
  const handleApiKeyClick = (apiKey: ApiKey) => {
    historyModeRef.current = 'push';
    if (tempApiKey && apiKey.key_id === tempApiKey.key_id) {
      // Clicking on temp key while creating
      setShowSidebar(true);
      return;
    }

    // Clear temp key if clicking on a real key
    if (tempApiKey) {
      setTempApiKey(null);
      setIsCreating(false);
    }

    setSelectedApiKey(apiKey);
    setShowSidebar(true);
  };

  // Handle create new API key
  const handleCreateApiKey = () => {
    historyModeRef.current = 'push';
    const newTempApiKey: ApiKey = {
      key_id: `temp_${Date.now()}`,
      key_prefix: '',
      name: '',
      scopes: ['read'],
      created_at: new Date().toISOString(),
      last_used_at: null,
      revoked_at: null,
      expires_at: null,
      is_active: true
    };

    setTempApiKey(newTempApiKey);
    setSelectedApiKey(newTempApiKey);
    setIsCreating(true);
    setShowSidebar(true);
  };

  // Handle close sidebar
  const handleCloseSidebar = () => {
    historyModeRef.current = 'push';
    setShowSidebar(false);
    setIsCreating(false);

    // Clear temp key when closing
    if (tempApiKey) {
      setTempApiKey(null);
      setSelectedApiKey(null);
    }
  };

  // Handle save API key
  const handleSaveApiKey = (savedApiKey: ApiKey) => {
    if (isCreating) {
      // Add new API key to list
      setApiKeys(prev => [savedApiKey, ...prev]);
      setTempApiKey(null);
      setIsCreating(false);
      setSelectedApiKey(savedApiKey);
    } else {
      // Rotation returns a new key ID. Replace the selected predecessor rather
      // than leaving its now-revoked card as the apparent source of truth.
      const replacementKeyId = selectedApiKey?.key_id || savedApiKey.key_id;
      setApiKeys((prev) => replaceSelectedApiKey(
        prev,
        savedApiKey,
        replacementKeyId,
      ));
      setSelectedApiKey(savedApiKey);
    }
  };

  // Handle delete API key
  const handleDeleteApiKey = (keyId: string) => {
    historyModeRef.current = 'push';
    setApiKeys(prev => prev.filter(k => k.key_id !== keyId));
    setSelectedApiKey(null);
    setShowSidebar(false);
  };

  // Filter and sort API keys
  const filteredApiKeys = useMemo(() => {
    let filtered = [...apiKeys];

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(k =>
        k.name.toLowerCase().includes(term) ||
        k.key_prefix.toLowerCase().includes(term) ||
        k.scopes.some(s => s.toLowerCase().includes(term))
      );
    }

    // Apply status filter
    if (filterStatus === 'active') {
      filtered = filtered.filter(k => k.is_active && !k.revoked_at);
    } else if (filterStatus === 'revoked') {
      filtered = filtered.filter(k => k.revoked_at);
    } else if (filterStatus === 'expired') {
      filtered = filtered.filter(k => k.expires_at && new Date(k.expires_at) < new Date());
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'created_at':
          comparison = new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime();
          break;
        case 'last_used_at':
          const aTime = a.last_used_at ? new Date(a.last_used_at).getTime() : 0;
          const bTime = b.last_used_at ? new Date(b.last_used_at).getTime() : 0;
          comparison = aTime - bTime;
          break;
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [apiKeys, searchTerm, filterStatus, sortBy, sortOrder]);

  // All API keys to display (including temp)
  const displayApiKeys = useMemo(() => {
    if (tempApiKey) {
      return [tempApiKey, ...filteredApiKeys];
    }
    return filteredApiKeys;
  }, [tempApiKey, filteredApiKeys]);

  // Calculate container margin based on sidebar
  const containerStyle = useMemo(() => {
    if (showSidebar) {
      return { marginRight: `${sidebarWidth}px` };
    }
    return {};
  }, [showSidebar, sidebarWidth]);

  return (
    <div className="apikeys-tab">
      <div
        className="apikeys-container"
        style={containerStyle}
      >
        {urlNotice && (
          <div className="error-message">
            {urlNotice}
            <button onClick={() => setUrlNotice(null)}>×</button>
          </div>
        )}
        {/* Section Header */}
        <div className="apikeys-section">
          <div className="section-header">
            <div className="section-title">
              <Key size={20} />
              <h2>API Keys</h2>
              <span className="apikey-count">{apiKeys.length} keys</span>
            </div>

            <div className="section-controls">
              {/* Search */}
              <div className="search-input-wrapper">
                <Search size={16} />
                <input
                  type="text"
                  placeholder="Search API keys..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
              </div>

              {/* Status Filter */}
              <CustomSelect
                value={filterStatus}
                onChange={setFilterStatus}
                options={[
                  { value: '', label: 'All Keys' },
                  { value: 'active', label: 'Active' },
                  { value: 'revoked', label: 'Revoked' },
                  { value: 'expired', label: 'Expired' }
                ]}
              />

              {/* Sort */}
              <div className="sort-controls-custom">
                <CustomSelect
                  value={sortBy}
                  onChange={(value) => setSortBy(value as any)}
                  className="sort-dropdown"
                  options={[
                    { value: 'created_at', label: 'Date Created' },
                    { value: 'name', label: 'Name' },
                    { value: 'last_used_at', label: 'Last Used' }
                  ]}
                />
                <button
                  className="sort-order-btn"
                  onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                  title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
                >
                  {sortOrder === 'asc' ? '↑' : '↓'}
                </button>
              </div>

              {/* Refresh */}
              <button
                className="refresh-btn"
                onClick={loadApiKeys}
                disabled={isLoading}
                title="Refresh API keys"
              >
                <RefreshCw size={16} className={isLoading ? 'spinning' : ''} />
              </button>

              {/* New API Key */}
              <button
                className="new-apikey-btn"
                onClick={handleCreateApiKey}
                disabled={isCreating}
              >
                <Plus size={16} />
                New API Key
              </button>
            </div>
          </div>

          {/* API Keys Grid */}
          <div className="apikeys-grid">
            {isLoading && apiKeys.length === 0 ? (
              <div className="loading-state">
                <RefreshCw size={24} className="spinning" />
                <span>Loading API keys...</span>
              </div>
            ) : displayApiKeys.length === 0 ? (
              <div className="empty-state">
                <Key size={48} />
                <h3>No API Keys</h3>
                <p>Create your first API key to authenticate programmatic access to your account.</p>
                <button className="create-first-btn" onClick={handleCreateApiKey}>
                  <Plus size={16} />
                  Create API Key
                </button>
              </div>
            ) : (
              displayApiKeys.map(apiKey => (
                <ApiKeyCard
                  key={apiKey.key_id}
                  apiKey={apiKey}
                  isSelected={selectedApiKey?.key_id === apiKey.key_id}
                  onClick={() => handleApiKeyClick(apiKey)}
                  isCreating={apiKey.key_id === tempApiKey?.key_id}
                />
              ))
            )}
          </div>
        </div>
      </div>

      {/* Detail Sidebar */}
      <ApiKeyDetailSidebar
        apiKey={selectedApiKey}
        isOpen={showSidebar}
        onClose={handleCloseSidebar}
        onSave={handleSaveApiKey}
        onDelete={handleDeleteApiKey}
        isCreating={isCreating}
      />
    </div>
  );
};

export default ApiKeysTab;
